'use client'

import { useProductCardAddToCart } from '@/feature/product/hooks/useProductCardAddToCart'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useNextIsClientHydrated } from '@/lib/next/useNextIsClientHydrated'
import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon } from '@/ui/icons/icon/Icon'
import type { ProductCard_AddToCartFragment } from '@gql-generated/graphql'
import type { PressEvent } from 'react-aria'

export type ProductCardAddToCartButtonProps = {
  // Product to add to cart, dedicated add-to-cart fragment
  product: ProductCard_AddToCartFragment

  // Analytics source, reuses the ProductCard source prop
  source?: string

  // Additional classes, positioning (absolute) owned by the caller
  className?: string
}

// Quick add-to-cart CTA shown on hover of an article card (non-touch), hidden at rest via invisible + opacity-0
export function ProductCardAddToCartButton({
  product,
  source,
  className,
}: ProductCardAddToCartButtonProps): React.ReactElement | null {
  // Deferred to the client: canAddToCart depends on the cart id (client cache), avoids a bundle-overlay hydration mismatch
  const isHydrated = useNextIsClientHydrated()
  const { t } = useTranslation(['generic'])
  const { addToCart, isLoading, isDisabled, canAddToCart } = useProductCardAddToCart({
    product,
    source,
  })

  if (!isHydrated || !canAddToCart) {
    return null
  }

  return (
    <Button
      variant="primary"
      iconLeft={<Icon.ShoppingBasket />}
      onPress={addToCart}
      onPressEnd={(e: PressEvent) => {
        // Blur so :focus-within releases the card's hover state (stroke/CTA/stock fade) after the click
        // onPress itself never carries the event through (ButtonV2/AbstractButton both strip it), onPressEnd does
        if (e.target instanceof HTMLElement) {
          e.target.blur()
        }
      }}
      isLoading={isLoading}
      isDisabled={isDisabled}
      intent={INTENT_SAME_AS_WEB}
      data-testid="product-card/add-to-cart"
      className={twMerge(
        'invisible opacity-0 transition-[opacity,visibility]',
        'group-data-[hovered]/product-card:visible group-data-[hovered]/product-card:opacity-100',
        'mouse-only:group-hover/product-card:visible mouse-only:group-hover/product-card:opacity-100',
        'group-focus-within/product-card:visible group-focus-within/product-card:opacity-100',
        className,
        // !absolute forces the win over AbstractButton's .btn (position: relative, a separate stylesheet twMerge can't see)
        '!absolute inset-x-0 bottom-0 w-full',
      )}
    >
      {t('generic:addToCart.ctaLabel')}
    </Button>
  )
}
